import { initBlurUp } from "./blur-up";

let clickHandler = null;
let keydownHandler = null;
let popstateHandler = null;
let activeOverlay = null;
let activeOverlayUrl = null;
let activeBaseUrl = null;
let backgroundScrollY = 0;
let isClosing = false;
const INLINE_OVERLAY_STATE_KEY = "__inlineOverlay";

function getAnimSpeedMs() {
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue("--anim-speed")
    .trim();
  const value = Number.parseFloat(raw);

  if (!Number.isFinite(value) || value <= 0) return 400;
  if (raw.endsWith("ms")) return value;
  return value * 1000;
}

function cleanupOverlayElement() {
  if (activeOverlay && activeOverlay.isConnected) {
    activeOverlay.remove();
  }
  activeOverlay = null;
  activeOverlayUrl = null;
  activeBaseUrl = null;
  backgroundScrollY = 0;
  isClosing = false;
}

function closeInlineOverlay() {
  if (!(activeOverlay instanceof HTMLElement) || isClosing) return;
  isClosing = true;
  activeOverlay.classList.add("is-closing");

  window.setTimeout(() => {
    const targetScrollY = backgroundScrollY;
    cleanupOverlayElement();
    window.requestAnimationFrame(() => {
      window.scrollTo(0, targetScrollY);
    });
  }, getAnimSpeedMs());
}

function isInlineOverlayState(state) {
  return Boolean(state && typeof state === "object" && state[INLINE_OVERLAY_STATE_KEY]);
}

function requestCloseInlineOverlay() {
  if (!(activeOverlay instanceof HTMLElement)) return;

  if (isInlineOverlayState(window.history.state)) {
    const nextState =
      window.history.state && typeof window.history.state === "object"
        ? { ...window.history.state }
        : {};
    delete nextState[INLINE_OVERLAY_STATE_KEY];
    delete nextState.__inlineOverlayBaseUrl;
    const url = activeBaseUrl || window.location.pathname + window.location.search + window.location.hash;
    window.history.replaceState(nextState, "", url);
  }

  closeInlineOverlay();
}

async function openInlineOverlay(url, { pushState = true } = {}) {
  const baseUrlBeforeOpen = window.location.href;
  const scrollBeforeOpen = window.scrollY || window.pageYOffset || 0;
  cleanupOverlayElement();

  const response = await fetch(url, {
    credentials: "same-origin",
    headers: {
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  if (!response.ok) {
    window.location.assign(url);
    return;
  }

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const sourceOverlay = doc.querySelector(".c-overlay");

  if (!(sourceOverlay instanceof HTMLElement)) {
    window.location.assign(url);
    return;
  }

  const overlay = document.importNode(sourceOverlay, true);
  overlay.classList.add("c-overlay--inline");
  overlay.classList.remove("is-closing");

  const closeLink = overlay.querySelector(".c-overlay__close");
  const curtain = overlay.querySelector(".c-overlay__curtain");

  if (!(closeLink instanceof HTMLAnchorElement) || !(curtain instanceof HTMLElement)) {
    window.location.assign(url);
    return;
  }

  closeLink.setAttribute("data-no-swup", "");

  closeLink.addEventListener("click", (event) => {
    event.preventDefault();
    requestCloseInlineOverlay();
  });

  curtain.addEventListener("click", (event) => {
    if (event.target !== curtain) return;
    event.preventDefault();
    requestCloseInlineOverlay();
  });

  document.body.appendChild(overlay);
  activeOverlay = overlay;
  activeOverlayUrl = new URL(url, window.location.href).href;
  activeBaseUrl = baseUrlBeforeOpen;
  backgroundScrollY = scrollBeforeOpen;
  initBlurUp(overlay);

  if (pushState) {
    const state = {
      ...(window.history.state && typeof window.history.state === "object"
        ? window.history.state
        : {}),
      [INLINE_OVERLAY_STATE_KEY]: true,
      __inlineOverlayBaseUrl: baseUrlBeforeOpen,
    };
    window.history.pushState(state, "", url);
  }
}

function shouldHandleOverlayClick(event, link) {
  if (!(link instanceof HTMLAnchorElement)) return false;
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (link.target && link.target !== "_self") return false;
  if (!link.hasAttribute("data-overlay")) return false;
  if (!link.href || link.href.startsWith("#")) return false;

  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin) return false;

  return true;
}

export function initInlineOverlay() {
  if (!clickHandler) {
    clickHandler = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const link = target.closest("a[data-overlay]");
      if (!shouldHandleOverlayClick(event, link)) return;

      event.preventDefault();
      openInlineOverlay(link.href, { pushState: true });
    };
    document.addEventListener("click", clickHandler);
  }

  if (!keydownHandler) {
    keydownHandler = (event) => {
      if (!(event instanceof KeyboardEvent) || event.key !== "Escape") return;
      if (!(activeOverlay instanceof HTMLElement)) return;
      event.preventDefault();
      requestCloseInlineOverlay();
    };
    document.addEventListener("keydown", keydownHandler);
  }

  if (!popstateHandler) {
    popstateHandler = () => {
      const currentHref = window.location.href;

      if (activeOverlay instanceof HTMLElement && activeOverlayUrl && currentHref !== activeOverlayUrl) {
        closeInlineOverlay();
        return;
      }

      if (isInlineOverlayState(window.history.state)) {
        if (!(activeOverlay instanceof HTMLElement)) {
          openInlineOverlay(window.location.href, { pushState: false });
        }
        return;
      }

      if (activeOverlay instanceof HTMLElement) {
        closeInlineOverlay();
      }
    };
    window.addEventListener("popstate", popstateHandler);
  }
}
